import {
  type ActivateGoalPackageRequest,
  type ActiveGoalPackageDTO,
  type CancelGoalPackageRequest,
  type GoalDiscoveryCheckpointRequest,
  type GoalDiscoveryCheckpointResponse,
  type GoalDiscoveryDraftDTO,
  type GoalDiscoveryDraftUpdate,
  type GoalMeasureDTO,
  type GoalMeasureDraft,
  type GoalsExperienceResponse,
  type UpdateGoalMetricCheckInRequest
} from '../../../models/goal';
import { baseGoalsExperience } from '../fixtures/goals';
import { clone } from '../support/clone';
import { nowIso } from '../support/date-utils';
import { getScenario } from '../support/scenarios';
import { readStorage, storageKeys, writeStorage } from '../support/storage';

// --- Normalization Helpers ---

/**
 * Normalizes a goal measure draft from string or partial DTO.
 */
function normalizeGoalMeasureDraft(
  measure: GoalMeasureDraft | string,
  type: 'leading' | 'lagging',
  index: number
): GoalMeasureDraft {
  // WHY: Support simple string inputs from early discovery phases while maintaining DTO contract
  if (typeof measure === 'string') {
    return {
      id: `measure-${type}-${index}`,
      type,
      label: measure,
      review: { judgment: null, feedback: '' }
    };
  }

  return {
    id: measure.id ?? `measure-${type}-${index}`,
    type: measure.type ?? type,
    label: measure.label,
    description: measure.description,
    targetValue: measure.targetValue,
    targetUnit: measure.targetUnit,
    minValue: measure.minValue,
    maxValue: measure.maxValue,
    review: measure.review ?? { judgment: null, feedback: '' }
  };
}

/**
 * Normalizes an active goal measure from partial DTO.
 */
function normalizeActiveGoalMeasure(
  measure: Partial<GoalMeasureDTO> & Pick<GoalMeasureDTO, 'label'>,
  type: 'leading' | 'lagging',
  index: number
): GoalMeasureDTO {
  return {
    id: measure.id ?? `measure-${type}-${index}`,
    type,
    label: measure.label,
    description: measure.description,
    target: measure.target,
    review: measure.review,
    latestCheckIn: measure.latestCheckIn ?? { status: null, note: '', updatedAt: null }
  };
}

/**
 * Normalizes the entire goals experience response.
 */
function normalizeGoalsExperience(experience: GoalsExperienceResponse): GoalsExperienceResponse {
  if (!experience.activePackage && !experience.draft) return experience;

  return {
    activePackage: experience.activePackage
      ? {
          ...experience.activePackage,
          leadingMeasures: experience.activePackage.leadingMeasures.map((m, i) =>
            normalizeActiveGoalMeasure(m, 'leading', i)
          ),
          laggingMeasures: experience.activePackage.laggingMeasures.map((m, i) =>
            normalizeActiveGoalMeasure(m, 'lagging', i)
          )
        }
      : null,
    draft: experience.draft
      ? {
          ...experience.draft,
          draftMeasures: experience.draft.draftMeasures
            ? {
                leading: experience.draft.draftMeasures.leading.map((m, i) =>
                  normalizeGoalMeasureDraft(m, 'leading', i)
                ),
                lagging: experience.draft.draftMeasures.lagging.map((m, i) =>
                  normalizeGoalMeasureDraft(m, 'lagging', i)
                )
              }
            : null,
          package: experience.draft.package
            ? {
                ...experience.draft.package,
                leadingMeasures: experience.draft.package.leadingMeasures.map((m, i) =>
                  normalizeGoalMeasureDraft(m, 'leading', i)
                ),
                laggingMeasures: experience.draft.package.laggingMeasures.map((m, i) =>
                  normalizeGoalMeasureDraft(m, 'lagging', i)
                )
              }
            : null
        }
      : null
  };
}

// --- Persistence Helpers ---

function getGoalsStorageKey(): string {
  const persona = getScenario('persona', 'mentee');
  const keyMap: Record<string, string> = {
    coach: storageKeys.goalsCoach,
    leader: storageKeys.goalsLeader,
    new: storageKeys.goalsNew,
    veteran: storageKeys.goalsVeteran,
    mentee: storageKeys.goals
  };
  return keyMap[persona] || storageKeys.goals;
}

function readExperience(): GoalsExperienceResponse {
  return readStorage(getGoalsStorageKey(), baseGoalsExperience);
}

function writeExperience(experience: GoalsExperienceResponse): void {
  writeStorage(getGoalsStorageKey(), experience);
}

// --- Mock Logic Helpers (Phase Transition) ---

function applyCheckpointMockData(phase: GoalDiscoveryCheckpointRequest['phase'], draft: GoalDiscoveryDraftDTO): void {
  switch (phase) {
    case 'concepts':
      draft.synthesisSummary = 'We found three distinct paths based on your role and objectives.';
      draft.goalConcepts = [
        {
          title: 'Become a leader who scales through delegation',
          description: 'Focus on building direct report capability and trust.',
          rationale: 'High-leverage for executive growth.'
        },
        {
          title: 'Become a strategic communicator',
          description: 'Master the art of high-stakes technical influence.',
          rationale: 'Crucial for cross-functional alignment.'
        },
        {
          title: 'Become an operational excellence anchor',
          description: 'Drive execution rigor and predictability.',
          rationale: 'Core to your current performance friction.'
        }
      ];
      break;

    case 'strategic-questions':
      draft.strategicQuestions = [
        'What is the smallest step you could take this week to delegate a decision?',
        'Who are the 2 stakeholders you need to influence most?',
        'If you could only solve one piece of friction, which would it be?'
      ];
      break;

    case 'refine':
      draft.refinedConcepts = [
        {
          title: 'Become a high-leverage leader who scales through manager development',
          description: 'A sharpened version focusing specifically on the manager layer.',
          rationale: 'Directly addresses your feedback on team execution.'
        }
      ];
      break;

    case 'metrics':
      draft.status = 'ready_for_activation';
      draft.package = {
        title: draft.package?.title || 'Executive Presence & Strategic Leadership',
        description: 'A comprehensive goal to enhance leadership visibility and strategic impact.',
        rationale: 'Alignment with Director level expectations.',
        leadingMeasures: [
          normalizeGoalMeasureDraft(
            {
              label: 'Weekly 1:1 completion rate',
              description: 'Protect a steady coaching rhythm.',
              targetValue: 4,
              targetUnit: 'per week'
            },
            'leading',
            0
          )
        ],
        laggingMeasures: [normalizeGoalMeasureDraft({ label: 'Team engagement score' }, 'lagging', 0)],
        plan: 'Weekly reviews.'
      };
      draft.draftMeasures = {
        leading: draft.package.leadingMeasures,
        lagging: draft.package.laggingMeasures
      };
      break;

    case 'package':
    case 'regenerate':
      draft.status = 'ready_for_activation';
      draft.package = {
        title: 'Executive Presence & Strategic Leadership',
        description: 'A comprehensive goal draft.',
        rationale: 'Alignment with Director expectations.',
        leadingMeasures: [normalizeGoalMeasureDraft('Weekly 1:1s', 'leading', 0)],
        laggingMeasures: [normalizeGoalMeasureDraft('Team engagement', 'lagging', 0)],
        plan: 'Weekly reviews.'
      };
      break;

    default:
      draft.synthesisSummary = 'We synthesized your direction based on your current input.';
  }
}

// --- Public Domain API ---

/**
 * Returns the normalized mock goals experience.
 */
export function getDevMockGoalsExperience() {
  return normalizeGoalsExperience(readExperience());
}

/**
 * Resets the mock goals experience to baseline.
 */
export function resetDevMockGoalsExperience() {
  return writeExperience(clone(baseGoalsExperience));
}

/**
 * Persists a goal-discovery checkpoint for the dev/preview path and returns the canned response.
 *
 * WHY: There is no real AI in preview mode. This applies the phase's deterministic mock data to the
 * stored draft. It is a MOCK — surfaced as such in the UI (GoalDiscoveryPage preview banner) — never
 * presented as a live result.
 *
 * `draftUpdate` is an optional caller-supplied override kept for the current transport signature;
 * when absent (the standard preview path), the phase's canned mock data is applied.
 */
export function saveDevMockGoalDiscoveryCheckpoint(
  request: GoalDiscoveryCheckpointRequest,
  draftUpdate?: GoalDiscoveryDraftUpdate
): GoalDiscoveryCheckpointResponse {
  const experience = getDevMockGoalsExperience();

  const nextDraft: GoalDiscoveryDraftDTO = experience.draft || {
    id: `draft-${Date.now()}`,
    status: 'in_progress',
    modelId: request.modelId,
    context: request.context,
    answers: request.answers,
    synthesisSummary: '',
    unresolvedQuestions: [],
    goalConcepts: [],
    refinedConcepts: [],
    strategicQuestions: [],
    package: null,
    updatedAt: nowIso()
  };

  nextDraft.answers = { ...nextDraft.answers, ...request.answers };
  nextDraft.context = request.context;
  nextDraft.modelId = request.modelId;
  nextDraft.updatedAt = nowIso();

  if (draftUpdate) {
    Object.assign(nextDraft, draftUpdate);
  } else {
    applyCheckpointMockData(request.phase, nextDraft);
  }

  writeExperience({ ...experience, draft: nextDraft });

  return {
    draft: nextDraft,
    ui: {
      synthesisSummary: nextDraft.synthesisSummary,
      unresolvedQuestions: nextDraft.unresolvedQuestions
    }
  };
}

/**
 * Explicit dev/preview transport for a goal-discovery checkpoint.
 *
 * WHY: Preview mode has no backend AI, so this returns the canned checkpoint response for the
 * requested phase by delegating to `saveDevMockGoalDiscoveryCheckpoint`. It is the INTENDED,
 * explicit dev path — not a fallback reached by catching a throwing "service". BH-AI-01's transport
 * calls this directly instead of catching an exception and substituting a mock.
 *
 * CONSTRAINT: Preview only. Production (`useLiveGoals`) never calls this and mock output is never
 * presented as live.
 */
export function generatePreviewCheckpoint(request: GoalDiscoveryCheckpointRequest): GoalDiscoveryCheckpointResponse {
  return saveDevMockGoalDiscoveryCheckpoint(request);
}

/**
 * Activates a goal package, moving it from draft to active.
 */
export function activateDevMockGoalPackage(request: ActivateGoalPackageRequest): ActiveGoalPackageDTO {
  const activePackage: ActiveGoalPackageDTO = {
    id: `goal-${Date.now()}`,
    status: 'active',
    title: request.package.title,
    description: request.package.description,
    rationale: request.package.rationale,
    leadingMeasures: request.package.leadingMeasures.map((m, i) => ({
      id: m.id ?? `measure-lead-${i}`,
      type: 'leading',
      label: m.label,
      description: m.description,
      target: m.targetValue !== undefined && m.targetUnit ? { value: m.targetValue, unit: m.targetUnit } : undefined,
      review: m.review,
      latestCheckIn: { status: null, note: '', updatedAt: null }
    })),
    laggingMeasures: request.package.laggingMeasures.map((m, i) => ({
      id: m.id ?? `measure-lag-${i}`,
      type: 'lagging',
      label: m.label,
      description: m.description,
      target: m.targetValue !== undefined && m.targetUnit ? { value: m.targetValue, unit: m.targetUnit } : undefined,
      review: m.review,
      latestCheckIn: { status: null, note: '', updatedAt: null }
    })),
    plan: { cadence: 'weekly', note: request.package.plan },
    updatedAt: nowIso()
  };

  writeExperience({ activePackage, draft: null });
  return activePackage;
}

/**
 * Cancels an active goal package.
 */
export function cancelDevMockGoalPackage(_request: CancelGoalPackageRequest): GoalsExperienceResponse {
  const nextExperience = { ...getDevMockGoalsExperience(), activePackage: null };
  writeExperience(nextExperience);
  return nextExperience;
}

/**
 * Updates a goal metric check-in.
 */
export function updateDevMockGoalMetricCheckIn(request: UpdateGoalMetricCheckInRequest): GoalsExperienceResponse {
  const experience = getDevMockGoalsExperience();
  const activePackage = experience.activePackage;

  if (!activePackage || activePackage.id !== request.goalId) return experience;

  const updateMetric = (m: GoalMeasureDTO) =>
    m.id === request.metricId
      ? { ...m, latestCheckIn: { status: request.status, note: request.note, updatedAt: nowIso() } }
      : m;

  const nextExperience: GoalsExperienceResponse = {
    ...experience,
    activePackage: {
      ...activePackage,
      leadingMeasures: activePackage.leadingMeasures.map(updateMetric),
      laggingMeasures: activePackage.laggingMeasures.map(updateMetric),
      updatedAt: nowIso()
    }
  };

  writeExperience(nextExperience);
  return nextExperience;
}
